import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAuthModal } from '../contexts/AuthModalContext';
import { usePremiumStatus } from '../hooks/usePremiumStatus';
import Spinner from './Spinner';
import { hasActivePaidFeature } from '../utils/premiumAccess';

interface GoldMemberRouteProps {
  children: React.ReactNode;
  requireGold?: boolean;
}

const GoldMemberRoute: React.FC<GoldMemberRouteProps> = ({ 
  children,
  requireGold = true
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { openLogin } = useAuthModal();
  const { isPremium, isLoading: isPremiumLoading } = usePremiumStatus();
  const location = useLocation();

  const getPremiumFeature = (pathname: string) => {
    const normalizedPath = pathname.toLowerCase();

    if (normalizedPath.includes('/check-result')) {
      if (normalizedPath.includes('/jamb')) return { key: 'jambResultCheck', label: 'Check JAMB Results' };
      if (normalizedPath.includes('/waec')) return { key: 'waecResultCheck', label: 'Check WAEC Results' };
    }

    if (normalizedPath.includes('/mock-test/waec')) return { key: 'waecMockTest', label: 'WAEC Mock Test' };
    if (normalizedPath.includes('/mock-test/jamb')) return { key: 'jambMockTest', label: 'JAMB Mock Test' };
    if (normalizedPath.startsWith('/labs')) return { key: 'labs', label: 'Labs' };
    if (normalizedPath.startsWith('/language-hub')) return { key: 'languageHub', label: 'Language Hub' };
    if (normalizedPath.startsWith('/waec')) return { key: 'waec', label: 'Explore WAEC' };
    if (normalizedPath.startsWith('/jamb')) return { key: 'jamb', label: 'Explore JAMB' };
    return null;
  };

  // CRITICAL DEBUG: Log the auth and premium state
  console.debug('[GoldMemberRoute] State:', {
    isAuthenticated,
    isAuthLoading: isLoading,
    userExists: !!user,
    userSubscribed: user?.isSubscribed,
    userIsGoldMember: !!(user as any)?.isGoldMember || !!(user as any)?.isGold,
    userIsPremium: !!(user as any)?.isPremium,
    isPremium,
    isPremiumLoading
  });

  if (isLoading) {
    return <Spinner />;
  }

  // For gold-member/subscribed required routes
  if (requireGold) {
    if (!isAuthenticated) {
      // Open the login modal for unauthenticated users
      openLogin();
      // Do not render the protected page while login is in progress
      return null;
    }

    const feature = getPremiumFeature(location.pathname);
    if (!feature) {
      // If the route is not in the premium features list, allow access
      return <>{children}</>;
    }

    // Check both generic premium flags and per-feature payment flags
    if (user) {
      const isSubscribed = !!user.isSubscribed || !!(user as any).isSchoolSubscribed;
      const isGoldMember = !!(user as any).isGoldMember || !!(user as any).isGold;
      const hasFeatureAccess = hasActivePaidFeature(user.paidFeatures, user.paidFeatureExpiries, feature.key);
      const hasPremiumAccess = isSubscribed || isGoldMember || hasFeatureAccess;
      
      if (hasPremiumAccess) {
        console.debug('[GoldMemberRoute] User has premium access (local flags), allowing access:', { isSubscribed, isGoldMember, hasFeatureAccess, feature: feature.key });
        return <>{children}</>;
      }
    }

    // If premium status verification is still loading, show spinner
    if (isPremiumLoading) {
      console.debug('[GoldMemberRoute] Waiting for premium status verification...');
      return <Spinner />;
    }

    // If user is not premium and has not paid for this feature, redirect to payment
    const hasFeatureAccess = hasActivePaidFeature(user?.paidFeatures, user?.paidFeatureExpiries, feature.key);
    if (!hasFeatureAccess && !user?.isSubscribed && !(user as any)?.isSchoolSubscribed && !(user as any)?.isGoldMember && !(user as any)?.isGold) {
      console.debug('[GoldMemberRoute] User is not premium, redirecting to payment page', feature);
      return <Navigate to="/payment" state={{ from: location, featureKey: feature.key, featureLabel: feature.label, selectedFeatures: [feature.key] }} replace />;
    }

    // If premium status verified as true, allow access
    return <>{children}</>;
  }

  // For regular authenticated routes
  if (!isAuthenticated) {
    openLogin();
    return <>{children}</>;
  }

  return <>{children}</>;
};

export default GoldMemberRoute;